import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  forwardRef,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  FIELD_BORDER,
  FIELD_BORDER_ERROR,
  FIELD_ERROR,
  FIELD_HINT,
  FIELD_INPUT,
  FIELD_LABEL,
} from './field-styles';
import { uniqueId } from './ids';

export type FieldType = 'text' | 'email' | 'tel' | 'url' | 'search' | 'number';

/**
 * Campo di testo con etichetta, aiuto ed errore. È un `ControlValueAccessor`: si usa con
 * `formControlName` o `[formControl]`, e l'elemento `<input>` resta dentro la libreria.
 */
@Component({
  selector: 'bs-field',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => FieldComponent), multi: true },
  ],
  host: { class: 'flex flex-col gap-1.5' },
  template: `
    <label [for]="id" [class]="labelClass">
      {{ label() }}
      @if (required()) {
        <span class="text-stone-500 dark:text-stone-400" aria-hidden="true">*</span>
      }
    </label>
    <input
      #control
      [id]="id"
      [type]="type()"
      [class]="inputClass()"
      [value]="value()"
      [disabled]="disabled()"
      [readOnly]="readonly()"
      [required]="required()"
      [attr.name]="name()"
      [attr.autocomplete]="autocomplete()"
      [attr.inputmode]="inputmode()"
      [attr.spellcheck]="type() === 'email' ? 'false' : null"
      [attr.aria-invalid]="error() ? 'true' : null"
      [attr.aria-describedby]="describedBy()"
      [attr.data-testid]="testId()"
      (input)="onInput($event)"
      (blur)="onTouched()"
    />
    @if (hint(); as text) {
      <p [id]="hintId" [class]="hintClass">{{ text }}</p>
    }
    @if (error(); as message) {
      <p
        [id]="errorId"
        [class]="errorClass"
        [attr.data-testid]="testId() ? testId() + '-error' : null"
      >
        {{ message }}
      </p>
    }
  `,
})
export class FieldComponent implements ControlValueAccessor {
  readonly label = input.required<string>();
  readonly type = input<FieldType>('text');
  readonly hint = input<string | null>(null);
  readonly error = input<string | null>(null);
  readonly mono = input(false);
  readonly required = input(false);
  readonly readonly = input(false);
  readonly name = input<string | null>(null);
  readonly autocomplete = input<string | null>(null);
  readonly inputmode = input<string | null>(null);
  readonly testId = input<string | null>(null);

  protected readonly id = uniqueId('bs-field');
  protected readonly hintId = `${this.id}-hint`;
  protected readonly errorId = `${this.id}-error`;
  protected readonly labelClass = FIELD_LABEL;
  protected readonly hintClass = FIELD_HINT;
  protected readonly errorClass = FIELD_ERROR;

  protected readonly value = signal('');
  protected readonly disabled = signal(false);
  private readonly control = viewChild.required<ElementRef<HTMLInputElement>>('control');

  protected readonly inputClass = computed(
    () =>
      `${FIELD_INPUT} ${this.error() ? FIELD_BORDER_ERROR : FIELD_BORDER} ${this.mono() ? 'font-mono' : ''}`,
  );
  protected readonly describedBy = computed(() => {
    const ids = [this.hint() ? this.hintId : null, this.error() ? this.errorId : null];
    return ids.filter(Boolean).join(' ') || null;
  });

  private onChange: (value: string) => void = () => undefined;
  protected onTouched: () => void = () => undefined;

  writeValue(value: unknown): void {
    this.value.set(
      typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '',
    );
  }
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(disabled: boolean): void {
    this.disabled.set(disabled);
  }

  focus(): void {
    this.control().nativeElement.focus();
  }

  protected onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.value.set(value);
    this.onChange(value);
  }
}
